import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { getStorageItem, isNative, setStorageItem } from '@/lib/capacitor';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';
import {
    checkTapToPaySupport,
    hasCompletedTapToPayOnboarding,
    tapToPayName,
    tapToPaySplashDescription,
} from '@/lib/tapToPay';
import { useTapToPaySetup } from '@/hooks/useTapToPaySetup';
import { TapToPayIcon } from './ui/icons/TapToPayIcon';
import { LoadingSpinner } from './ui/LoadingSpinner';

/**
 * One-time full-screen introduction to Tap to Pay on iPhone (App Review checklist
 * §3.1, which also satisfies Marketing requirement 6.2). Shown once to every
 * eligible merchant who hasn't already enrolled, using value-proposition copy from
 * Apple's Tap to Pay on iPhone Marketing Guide.
 *
 * "Set up now" runs the same enrolment as everywhere else, so this doubles as an
 * early §3.3 Terms-acceptance entry point. Deliberately renders nothing until the
 * seen-flag has been read, so it never flashes on a subsequent launch.
 */
const SPLASH_SEEN_KEY = 'tap_to_pay_splash_seen_v1';

export function TapToPaySplash() {
    const { isAuthenticated } = useAuth();
    const { selectedCompanyId } = useGlobalCompany();
    const { canUseTapToPay } = useJobEditPermissions();
    const queryClient = useQueryClient();

    const [eligible, setEligible] = useState(false);
    const [terminalLocationId, setTerminalLocationId] = useState<string | undefined>();
    const [visible, setVisible] = useState(false);

    const tapToPaySetup = useTapToPaySetup(terminalLocationId);

    useEffect(() => {
        if (!isNative() || !isAuthenticated || !selectedCompanyId || !canUseTapToPay) return;

        let cancelled = false;
        void (async () => {
            // Never re-show, and never show to a phone that has already enrolled —
            // the checkout and settings entry points cover them from here on.
            if ((await getStorageItem(SPLASH_SEEN_KEY)) === 'true') return;
            if (await hasCompletedTapToPayOnboarding()) return;

            const support = await checkTapToPaySupport();
            if (!support.supported) return;

            let locationId: string | undefined;
            try {
                const status = await queryClient.fetchQuery({
                    queryKey: ['paymentProviderStatus', selectedCompanyId],
                    queryFn: () => apiService.getPaymentProviderStatus(),
                    staleTime: 5 * 60 * 1000,
                });
                // Only pitch it to merchants who can actually turn it on now.
                if (!status?.connected || !status?.charges_enabled || !status?.terminal_location_id) return;
                locationId = status.terminal_location_id;
            } catch {
                return;
            }

            if (cancelled) return;
            setTerminalLocationId(locationId);
            setEligible(true);
            setVisible(true);
        })();

        return () => { cancelled = true; };
    }, [isAuthenticated, selectedCompanyId, canUseTapToPay, queryClient]);

    const dismiss = () => {
        setVisible(false);
        void setStorageItem(SPLASH_SEEN_KEY, 'true');
    };

    if (!eligible || !visible) return null;

    const name = tapToPayName();

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm relative animate-in fade-in zoom-in-95 duration-200">
                <div className="p-8 flex flex-col items-center text-center">
                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-purple-100 text-purple-600 mb-5">
                        <TapToPayIcon className="h-11 w-11" />
                    </div>

                    <h2 className="text-xl font-bold text-gray-900">Accept payments with {name}</h2>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                        {tapToPaySplashDescription()}
                    </p>

                    {tapToPaySetup.message && (
                        <p className={`mt-4 text-xs ${tapToPaySetup.status === 'error' ? 'text-error-600' : 'text-purple-700'}`}>
                            {tapToPaySetup.message}
                        </p>
                    )}

                    <div className="mt-6 w-full space-y-2">
                        {tapToPaySetup.status === 'done' ? (
                            <button
                                type="button"
                                onClick={dismiss}
                                className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                Done
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={tapToPaySetup.runSetup}
                                disabled={tapToPaySetup.status === 'running'}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {tapToPaySetup.status === 'running' && <LoadingSpinner />}
                                {tapToPaySetup.status === 'error' ? 'Try again' : 'Set up now'}
                            </button>
                        )}
                        {tapToPaySetup.status !== 'done' && (
                            <button
                                type="button"
                                onClick={dismiss}
                                className="w-full px-4 py-2.5 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
                            >
                                Not now
                            </button>
                        )}
                    </div>

                    <p className="mt-4 text-[11px] text-gray-400">
                        You can set this up any time from Settings → Payment provider.
                    </p>
                </div>
            </div>
        </div>
    );
}
